import { bodyOf, type Email, type EmailAddress } from "./jmap";
import { escapeHtml, htmlToText, textToHtml } from "./html-to-text";
import { sanitizeEmailHtml } from "./sanitize";

/**
 * Builds the quoted body of a reply or forward.
 *
 * The first version flattened the original to plain text and prefixed every
 * line with "> ", which is fine for a plain-text message and wrong for
 * everything else: replying to a formatted message threw away its tables,
 * links, images and emphasis, and the recipient got back a mangled transcript
 * of their own mail. Every other client quotes the original HTML.
 *
 * So an HTML original is quoted as HTML — sanitised first, because a reply
 * embeds a stranger's markup in a document we are about to send under our own
 * name — and a plain-text original is converted up. The text/plain alternative
 * is derived from the result downstream, so both parts stay consistent without
 * being built twice.
 */

function displayName(address: EmailAddress | undefined): string {
  return address?.name || address?.email || "someone";
}

/** "On <date>, <name> wrote:" — the attribution every client recognises. */
function attribution(email: Email): string {
  const when = new Date(email.receivedAt).toLocaleString();
  return `On ${when}, ${displayName(email.from?.[0])} wrote:`;
}

function addressList(addresses: EmailAddress[] | null | undefined): string {
  return (addresses ?? [])
    .map((a) => (a.name ? `${a.name} <${a.email}>` : a.email))
    .join(", ");
}

/**
 * The forwarded-message header block.
 *
 * A forward without From/Date/Subject/To is just an unattributed wall of text;
 * the recipient cannot tell who wrote it or when.
 */
function forwardHeader(email: Email): string {
  const rows: [string, string][] = [
    ["From", addressList(email.from)],
    ["Date", new Date(email.receivedAt).toLocaleString()],
    ["Subject", email.subject ?? ""],
    ["To", addressList(email.to)],
  ];
  if (email.cc?.length) rows.push(["Cc", addressList(email.cc)]);

  return (
    `<div>---------- Forwarded message ----------</div>` +
    rows
      .map(([label, value]) => `<div><b>${label}:</b> ${escapeHtml(value)}</div>`)
      .join("")
  );
}

/** The original's body as HTML, whatever form it arrived in. */
function originalAsHtml(email: Email): string {
  const body = bodyOf(email);
  if (body.html) {
    // Sanitised even though it is going into our own composer: this markup is
    // about to be sent out under the sender's name, and a reply is the last
    // place to be relaying someone else's script or tracking pixel.
    return sanitizeEmailHtml(body.html).html;
  }
  return textToHtml(body.text ?? email.preview ?? "");
}

export interface QuotedBody {
  /** The composer's starting HTML, with the caret's blank line at the top. */
  html: string;
  /** The plain-text equivalent, for callers that still want one. */
  text: string;
}

export function quoteForReply(email: Email): QuotedBody {
  const html =
    `<div><br></div>` +
    `<div>${escapeHtml(attribution(email))}</div>` +
    `<blockquote>${originalAsHtml(email)}</blockquote>`;
  return { html, text: htmlToText(html) };
}

export function quoteForForward(email: Email): QuotedBody {
  const html =
    `<div><br></div>` +
    forwardHeader(email) +
    `<div><br></div>` +
    originalAsHtml(email);
  return { html, text: htmlToText(html) };
}
